import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

var SERVICE_CONFIG = {
  netflix:        { label: "Netflix",      borderColor: "#E50914", logo: "/netflix.png",        w: 80,  h: 80  },
  hulu:           { label: "Hulu",         borderColor: "#1CE783", logo: "/hulu.svg",            w: 110, h: 38  },
  disney_plus:    { label: "Disney+",      borderColor: "#4A90D9", logo: "/disney-plus.svg",     w: 200, h: 110 },
  hbo_max:        { label: "Max",          borderColor: "#6B2FBA", logo: "/max.svg",             w: 110, h: 60  },
  amazon_prime:   { label: "Prime Video",  borderColor: "#00A8E1", logo: "/prime-video.svg",     w: 90,  h: 90  },
  apple_tv:       { label: "Apple TV+",    borderColor: "#cccccc", logo: "/apple-tv.png",        w: 90,  h: 90  },
  peacock:        { label: "Peacock",      borderColor: "#ffffff", logo: "/peacock.svg",         w: 110, h: 38  },
  paramount_plus: { label: "Paramount+",   borderColor: "#0064FF", logo: "/paramount-plus.svg",  w: 90,  h: 70  },
  espn_plus:      { label: "ESPN+",        borderColor: "#FF0000", logo: "/espn.png",            w: 120,  h: 90  },
  crunchyroll:    { label: "Crunchyroll",  borderColor: "#F47521", logo: "/crunchyroll.png",     w: 110,  h: 110  },
};

var TIER_LIMITS = { free: 2, basic: 4, standard: 6, premium: 999 };

export default function Services() {
  const { getToken, isSignedIn, loading: authLoading } = useAuth();
  const [connectedServices, setConnectedServices] = useState([]);
  const [userTier, setUserTier] = useState("free");
  const [apiLoading, setApiLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(function() {
    axios.get(BACKEND_URL + "/api/health", { timeout: 30000 }).catch(function() {});
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      setApiLoading(true);
      const token = await getToken();
      if (!token) { setApiLoading(false); return; }
      const res = await axios.get(BACKEND_URL + "/api/auth/me", {
        headers: { Authorization: "Bearer " + token },
        timeout: 30000,
      });
      setConnectedServices(res.data.connected_services || []);
      setUserTier(res.data.subscription_tier || "free");
    } catch (err) {
    } finally {
      setApiLoading(false);
    }
  }, [getToken]);

  useEffect(function() {
    if (isSignedIn && !authLoading) fetchUserData();
  }, [fetchUserData, isSignedIn, authLoading]);

  async function handleToggle(serviceKey) {
    var isConnected = connectedServices.includes(serviceKey);
    var limit = TIER_LIMITS[userTier] || 2;
    if (!isConnected && connectedServices.length >= limit) return;
    try {
      setActionLoading(serviceKey);
      const token = await getToken();
      var res;
      if (isConnected) {
        res = await axios.delete(BACKEND_URL + "/api/services/disconnect/" + serviceKey,
          { headers: { Authorization: "Bearer " + token }, timeout: 30000 });
      } else {
        res = await axios.post(BACKEND_URL + "/api/services/connect/" + serviceKey, {},
          { headers: { Authorization: "Bearer " + token }, timeout: 30000 });
      }
      setConnectedServices(res.data.connected_services || []);
      // Tell Android to trigger accessibility onboarding after first service connect
      if (!isConnected && window.QudeAndroid?.onFirstServiceConnected) {
        window.QudeAndroid.onFirstServiceConnected();
      }
    } catch (err) {
    } finally {
      setActionLoading(null);
    }
  }

  var limit = TIER_LIMITS[userTier] || 2;
  var atLimit = !apiLoading && connectedServices.length >= limit;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.heading}>Streaming Services</h1>
        <p style={styles.subheading}>
          {apiLoading ? "Connecting..." : connectedServices.length + " / " + (limit >= 999 ? "\u221e" : limit) + " connected"}
          {"  \u00b7  "}
          <span style={styles.tierBadge}>{userTier.charAt(0).toUpperCase() + userTier.slice(1)}</span>
        </p>
        {!apiLoading && atLimit && userTier !== "premium" && (
          <div style={styles.upgradeBox}>
            <p style={styles.upgradeText}>
              {"You've hit your " + userTier + " limit. "}
              <a href="/pricing" style={styles.upgradeLink}>Upgrade to add more</a>
            </p>
          </div>
        )}
        <div style={styles.grid}>
          {Object.entries(SERVICE_CONFIG).map(function(entry) {
            var key = entry[0];
            var config = entry[1];
            var isConnected = connectedServices.includes(key);
            var isLoading = actionLoading === key;
            var isDisabled = !isConnected && atLimit;
            var isAppleTV = key === "apple_tv";
            return (
              <div
                key={key}
                onClick={function() { if (!isLoading && !apiLoading) handleToggle(key); }}
                style={Object.assign({}, styles.card, {
                  opacity: isLoading ? 0.6 : isDisabled ? 0.3 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  background: isConnected
                    ? config.borderColor + "12"
                    : "rgba(255,255,255,0.03)",
                  boxShadow: isConnected
                    ? "0 0 32px " + config.borderColor + "44, inset 0 0 40px " + config.borderColor + "08"
                    : "none",
                  border: isConnected
                    ? "1.5px solid " + config.borderColor + "80"
                    : "1.5px solid rgba(255,255,255,0.06)",
                })}
              >
                {isLoading && (
                  <div style={styles.loadingOverlay}>
                    <div style={styles.spinner} />
                  </div>
                )}
                <img
                  src={config.logo}
                  alt={config.label}
                  style={{
                    width: config.w,
                    height: config.h,
                    objectFit: "contain",
                    mixBlendMode: isAppleTV ? "lighten" : "normal",
                    filter: isConnected
                      ? "drop-shadow(0 0 10px " + config.borderColor + "99) brightness(1.1)"
                      : "brightness(0.75) saturate(0.6)",
                    transition: "all 0.25s ease",
                  }}
                  onError={function(e) {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                <span style={{ display: "none", color: "#ffffff", fontSize: 14, fontWeight: 700 }}>{config.label}</span>
              </div>
            );
          })}
        </div>
        <p style={styles.hint}>Tap a service to connect · connected services glow</p>
      </div>
    </div>
  );
}

var styles = {
  page: {
    minHeight: "100vh",
    padding: "32px 16px",
    fontFamily: "Inter, -apple-system, sans-serif",
    color: "#ffffff",
  },
  container: { maxWidth: 860, margin: "0 auto" },
  heading: { fontSize: 28, fontWeight: 700, margin: "0 0 6px 0", color: "#ffffff" },
  subheading: { fontSize: 14, color: "#888888", margin: "0 0 24px 0" },
  tierBadge: {
    backgroundColor: "#7c3aed",
    color: "#ffffff",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },
  upgradeBox: {
    backgroundColor: "rgba(124,58,237,0.08)",
    border: "1px solid rgba(124,58,237,0.25)",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 24,
  },
  upgradeText: { color: "#aaaaaa", fontSize: 14, margin: 0 },
  upgradeLink: { color: "#a78bfa", textDecoration: "none", fontWeight: 600 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 14,
  },
  card: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    borderRadius: 20,
    height: 160,
    transition: "all 0.25s ease",
    overflow: "hidden",
    backdropFilter: "blur(8px)",
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.4)",
    borderRadius: 20,
    zIndex: 2,
  },
  spinner: {
    width: 24,
    height: 24,
    border: "2px solid rgba(255,255,255,0.2)",
    borderTop: "2px solid #ffffff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  hint: { textAlign: "center", color: "#444", fontSize: 12, marginTop: 24 },
};
